import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { CreditCard, Languages, Mail, MapPin, Phone } from "lucide-react"

import { Badge } from "@repo/ui/components/ui/badge"
import { logger } from "@repo/utils/logger"
import { getExtendedResourceBySlugQueryOptions } from "~/modules/resources/resources.queries"
import { useLanguageOptions } from "~/modules/system-config/hooks/use-language-options"

const log = logger.child({
  module: "resources-resource-overview-route",
})

export const Route = createFileRoute(
  "/_protected-layout/_os-layout/resources/$resourceSlug/overview",
)({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    context.queryClient
      .ensureQueryData(
        getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
      )
      .catch((error) => {
        log.error("Error getting extended resource by slug", { error })
        return null
      })
  },
})

function RouteComponent() {
  const params = Route.useParams()
  const { data: resource } = useSuspenseQuery(
    getExtendedResourceBySlugQueryOptions({ slug: params.resourceSlug }),
  )

  const { toDisplayLanguage } = useLanguageOptions()

  if (!resource) {
    return <div>Resource not found</div>
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* Personal Information */}
      <div>
        <h3 className="mb-4 border-white/10 border-b pb-2 font-medium text-lg">
          Personal Information
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 h-5 w-5 text-white/70" />
            <div>
              <p className="text-sm text-white/70">Email</p>
              <p>{resource.email}</p>
            </div>
          </div>
          {resource.phone && (
            <div className="flex items-start gap-3">
              <Phone className="mt-0.5 h-5 w-5 text-white/70" />
              <div>
                <p className="text-sm text-white/70">Phone</p>
                <p>{resource.phone}</p>
              </div>
            </div>
          )}
          {resource.vat_number && (
            <div className="flex items-start gap-3">
              <CreditCard className="mt-0.5 h-5 w-5 text-white/70" />
              <div>
                <p className="text-sm text-white/70">VAT Number</p>
                <p>{resource.vat_number}</p>
              </div>
            </div>
          )}
          {resource.address_line_1 && (
            <div className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-5 w-5 text-white/70" />
              <div>
                <p className="text-sm text-white/70">Address</p>
                <p>
                  {resource.address_line_1}
                  {resource.address_line_2 && `, ${resource.address_line_2}`}
                  <br />
                  {resource.address_city}
                  {resource.address_state && `, ${resource.address_state}`}
                  <br />
                  {resource.address_postal_code}, {resource.address_country}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Banking Details */}
      <div>
        <h3 className="mb-4 border-white/10 border-b pb-2 font-medium text-lg">
          Billing Details
        </h3>
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div>
              <p className="text-sm text-white/70">Account Name</p>
              <p>{resource.banking_details_account_name}</p>
            </div>
          </div>
          {resource.banking_details_account_number && (
            <div className="flex items-start gap-3">
              <div>
                <p className="text-sm text-white/70">Account Number</p>
                <p>{resource.banking_details_account_number}</p>
              </div>
            </div>
          )}
          {resource.banking_details_bank_name && (
            <div className="flex items-start gap-3">
              <div>
                <p className="text-sm text-white/70">Bank Details</p>
                <p>
                  {resource.banking_details_bank_name}
                  {resource.banking_details_bank_address &&
                    `, ${resource.banking_details_bank_address}`}
                </p>
              </div>
            </div>
          )}
          {resource.banking_details_swift_code && (
            <div className="flex items-start gap-3">
              <div>
                <p className="text-sm text-white/70">SWIFT/BIC</p>
                <p>{resource.banking_details_swift_code}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Languages */}
      <div>
        <h3 className="mb-4 border-white/10 border-b pb-2 font-medium text-lg">
          Languages
        </h3>
        <div className="flex flex-wrap gap-2">
          {resource.resource_languages?.map((language) => (
            <div
              key={language.locale_code}
              className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2"
            >
              <Languages className="h-4 w-4 text-white/70" />
              <span>{toDisplayLanguage(language.locale_code)}</span>
              <Badge variant="secondary" className="ml-1">
                {language.proficiency}
              </Badge>
            </div>
          )) || <p className="text-sm text-white/70">No languages specified</p>}
        </div>
      </div>

      {/* Skills */}
      <div>
        <h3 className="mb-4 border-white/10 border-b pb-2 font-medium text-lg">
          Skills
        </h3>
        <div className="flex flex-wrap gap-2">
          {resource.skills?.map((skill) => (
            <Badge key={skill} variant="outline">
              {skill}
            </Badge>
          )) || <p className="text-sm text-white/70">No skills specified</p>}
        </div>
      </div>
    </div>
  )
}
